'use client';

import React from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Document, Page } from 'react-pdf';
import { Spinner } from '@heroui/spinner';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import { NextUI } from '@/components/NextUI';
import PdfFile from '@/components/Common/Icons/PdfFile';
import { useAppDispatch } from '@/hooks/redux';
import { showModal } from '@/store/slices/modalSlice';
import type { CheckoutReturnFile } from '@/store/slices/checkoutReturnSlice';
import { formatAnalyticsSource } from '@/helpers/common';
import formatSize from '@/helpers/format-size';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import { getKindAndBadge } from '@/features/documents/utils/fileKind';
import '@/features/documents/utils/pdfWorker';
import CloseCross from './ProceedPopupModal.icons/CloseCross';
import ChevronRight from './ProceedPopupModal.icons/ChevronRight';

export const PROCEED_POPUP_TRIGGER_SOURCE = 'proceed_popup';

const PREVIEW_WIDTH_DESKTOP = 358;
const PREVIEW_WIDTH_MOBILE = 179;
const MOBILE_BREAKPOINT = 768;

const getViewType = (): 'web' | 'mobile' =>
  typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
    ? 'mobile'
    : 'web';

const ProceedPopupModal = ({
  data,
  hideModal,
}: {
  data?: CheckoutReturnFile;
  hideModal: () => void;
}) => {
  const t = useTranslations('proceed_popup');
  const dispatch = useAppDispatch();
  const [numPages, setNumPages] = React.useState<number | null>(null);
  const [isPreviewFailed, setIsPreviewFailed] = React.useState(false);
  const [previewWidth, setPreviewWidth] = React.useState(PREVIEW_WIDTH_DESKTOP);

  const file = data?.file ?? null;
  const fileName = data?.fileName || file?.name || '';
  const extension = (data?.extension || fileName.split('.').pop() || '')
    .toLowerCase()
    .replace(/^\./, '');
  const { kind, badgeLabel, badgeColor } = React.useMemo(
    () => getKindAndBadge(fileName, file?.type ?? ''),
    [file?.type, fileName]
  );
  const isPdf = kind === 'pdf';
  const isImage = kind === 'image';
  const imageUrl = React.useMemo(
    () => (isImage && file ? URL.createObjectURL(file) : null),
    [file, isImage]
  );
  const formattedToolType = React.useMemo(
    () => formatAnalyticsSource(data?.toolType),
    [data?.toolType]
  );
  const buildAnalyticsSource = React.useCallback(
    () =>
      buildStructuredAnalyticsSource(data?.toolType, data?.originEntryPoint, {
        fileType: extension || undefined,
        viewType: getViewType(),
      }),
    [data?.originEntryPoint, data?.toolType, extension]
  );

  React.useEffect(() => {
    const updatePreviewWidth = () => {
      setPreviewWidth(
        window.innerWidth < MOBILE_BREAKPOINT
          ? PREVIEW_WIDTH_MOBILE
          : PREVIEW_WIDTH_DESKTOP
      );
    };

    updatePreviewWidth();
    window.addEventListener('resize', updatePreviewWidth);

    return () => window.removeEventListener('resize', updatePreviewWidth);
  }, []);

  React.useEffect(
    () => () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    },
    [imageUrl]
  );

  React.useEffect(() => {
    logHubxEvent({
      eventName: APP_EVENTS.LND_PROCEED_POPUP,
      source: formattedToolType,
      event_params: {
        source: buildAnalyticsSource(),
      },
    });
  }, []);

  const handleClose = React.useCallback(() => {
    hideModal();
  }, [hideModal]);

  const handleProceed = React.useCallback(() => {
    logHubxEvent({
      eventName: APP_EVENTS.BTN_PROCEED_POPUP,
      source: formattedToolType,
      event_params: {
        source: buildAnalyticsSource(),
      },
    });
    logHubxEvent({
      eventName: APP_EVENTS.CONVERSION_TRIGGER,
      event_params: { source: { value: PROCEED_POPUP_TRIGGER_SOURCE } },
    });
    dispatch(
      showModal({
        modalKey: 'PaddlePayment',
        modalData: {
          file,
          extension,
          toolType: data?.toolType,
          originEntryPoint: data?.originEntryPoint,
          selectedPlan: 'weekly',
          openedFromProceedPopup: true,
        },
      })
    );
  }, [
    buildAnalyticsSource,
    data?.originEntryPoint,
    data?.toolType,
    dispatch,
    extension,
    file,
    formattedToolType,
  ]);

  const summaryDetails = [
    isPdf && typeof numPages === 'number'
      ? t('page_count', { count: numPages })
      : null,
    file && file.size > 0 ? formatSize(file.size) : null,
  ].filter(Boolean);
  const showPdfPreview = isPdf && Boolean(file) && !isPreviewFailed;
  const showImagePreview = isImage && Boolean(imageUrl) && !isPreviewFailed;
  const showPlaceholder = !showPdfPreview && !showImagePreview;

  return (
    <NextUI.Modal
      className="mx-4 my-4 max-w-[870px] rounded-[16px] shadow-none sm:my-4"
      classNames={{
        content:
          'relative max-h-[calc(100dvh-32px)] overflow-y-auto overflow-x-hidden rounded-[16px] bg-white p-0 shadow-none md:overflow-hidden',
        backdrop: 'bg-[rgba(124,124,124,0.5)]',
      }}
      defaultOpen={true}
      name="ProceedPopup"
      backdrop="opaque"
      hideCloseButton={true}
      onClose={handleClose}
    >
      <div className="flex flex-col md:h-[570px] md:flex-row md:items-start">
        <div className="flex shrink-0 items-center justify-center bg-[#E4FFE1] p-4 md:h-full md:min-w-0 md:flex-1 md:p-8">
          <div className="relative h-[253px] w-[179px] shrink-0 md:h-[506px] md:w-[358px]">
            <div className="absolute inset-0 overflow-hidden rounded-[16px] bg-white">
              {showPdfPreview && file ? (
                <Document
                  file={file}
                  onLoadSuccess={(pdf) => setNumPages(pdf.numPages)}
                  onLoadError={() => setIsPreviewFailed(true)}
                  onSourceError={() => setIsPreviewFailed(true)}
                  onPassword={() => setIsPreviewFailed(true)}
                  loading={
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Spinner size="sm" />
                    </div>
                  }
                  error={null}
                >
                  <Page
                    pageNumber={1}
                    width={previewWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="!z-0"
                  />
                </Document>
              ) : null}

              {showImagePreview && imageUrl ? (
                <img
                  src={imageUrl}
                  alt={fileName}
                  onError={() => setIsPreviewFailed(true)}
                  className="h-full w-full object-cover"
                />
              ) : null}

              {showPlaceholder ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-lightest">
                  <div className="relative h-[124px] w-[96px]">
                    <Image
                      src="/icons/documents/doc-file.webp"
                      alt=""
                      fill
                      className="object-contain"
                      unoptimized
                    />
                    <span
                      className="absolute bottom-[4px] left-0 flex h-[26px] min-w-[68px] items-center justify-center rounded-[4px] px-2 text-[13px] font-bold leading-none text-white"
                      style={{ backgroundColor: badgeColor }}
                    >
                      {badgeLabel}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="pointer-events-none absolute inset-0 rounded-[16px] bg-gradient-to-b from-[rgba(255,255,255,0)] to-white shadow-[0px_12px_24px_0px_rgba(0,0,0,0.16)]" />

            {showPdfPreview ? (
              <div className="absolute bottom-2 right-2 rounded-[1000px] bg-[rgba(0,0,0,0.72)] px-2 py-1 font-barlow text-[15px] font-medium leading-[normal] text-white">
                1
                <span className="text-[rgba(255,255,255,0.64)]">
                  /{numPages ?? '-'}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col justify-center gap-4 p-4 md:h-full md:min-w-0 md:flex-1 md:gap-8 md:p-8">
          <div className="flex w-full flex-col items-center py-2 md:py-4">
            <div className="flex w-full flex-col gap-1">
              <span className="w-full font-barlow text-[22px] font-semibold leading-[normal] text-[#454655] md:text-[28px]">
                {t('title')}
              </span>
              <span className="w-full font-barlow text-[15px] font-normal leading-[normal] text-[#787887] md:text-[17px]">
                {t('description')}
              </span>
            </div>
          </div>

          <div className="flex w-full items-center gap-2 rounded-xl border border-solid border-[#E5E6EB] bg-[rgba(255,255,255,0.04)] px-4 py-3">
            {isPdf ? (
              <div className="relative h-9 w-9 shrink-0">
                <PdfFile className="absolute left-px top-0 h-9 w-[34px]" />
              </div>
            ) : (
              <span className="relative block h-9 w-9 shrink-0">
                <Image
                  src="/icons/documents/doc-file.webp"
                  alt=""
                  fill
                  className="object-contain"
                  unoptimized
                />
                <span
                  className="absolute bottom-[3px] left-0 flex h-[12px] min-w-[28px] items-center justify-center rounded-[2px] px-0.5 text-[7px] font-semibold leading-none text-white"
                  style={{ backgroundColor: badgeColor }}
                >
                  {badgeLabel}
                </span>
              </span>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span
                className="truncate font-barlow text-[17px] font-normal leading-[normal] text-[#454655]"
                title={fileName}
              >
                {fileName}
              </span>
              {summaryDetails.length > 0 ? (
                <span className="truncate font-barlow text-[15px] font-normal leading-[normal] text-[#787887]">
                  {summaryDetails.join(' • ')}
                </span>
              ) : null}
            </div>
          </div>

          <button
            type="button"
            onClick={handleProceed}
            className={clsx(
              'flex h-12 w-full items-center justify-center gap-1 rounded-[5px] bg-[#4B7EFF] p-2 font-barlow text-[17px] font-medium leading-[normal] text-white outline-none transition-colors hover:bg-primary-dark-blue'
            )}
          >
            {t('cta')}
            <span className="flex h-6 w-6 shrink-0 items-center justify-center">
              <ChevronRight className="h-[10.12px] w-[6.12px]" />
            </span>
          </button>
        </div>
      </div>

      <button
        type="button"
        aria-label={t('close')}
        onClick={handleClose}
        className="absolute right-5 top-5 z-10 flex h-8 w-8 items-center justify-center text-[#454655] outline-none"
      >
        <CloseCross className="h-[13.33px] w-[13.33px]" />
      </button>
    </NextUI.Modal>
  );
};

export default ProceedPopupModal;
